import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingChipsWithArrow from 'template-collectibles/publishing-ui/TemplatePublishingChipsWithArrow';
import {
  PublishableVersionStatus,
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  DEFAULT_VERSION_NAME,
  PublishableVersionAction,
  CONTROLLED_VERSION_NAME_PREFIX,
  AUTO_GENERATED_VERSION_NAME_PREFIX,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import TextArea from 'ui-library/bs/TextArea';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

function getButtonPropsFromAction(action: PublishableVersionAction, hasError?: boolean): ModalButton {
  if (action === PublishableVersionAction.PUBLISH) {
    return {text: 'Publish', submit: true, disabled: hasError};
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return {
      text: 'Withdraw',
      submit: true,
      className: css(styles.withdrawActionButton),
    };
  }
  throw new Error(`Invalid action: ${action}`);
}

function getTitleFromAction(action: PublishableVersionAction): string {
  if (action === PublishableVersionAction.PUBLISH) {
    return `Publish version`;
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return `Withdraw version`;
  }
  throw new Error(`Invalid action: ${action}`);
}

function getDescriptionFromAction(
  action: PublishableVersionAction,
  publishableModelTypeName: string,
  hasEffectiveVersion: boolean
): JSX.Element {
  if (action === PublishableVersionAction.PUBLISH) {
    if (hasEffectiveVersion) {
      return (
        <p>
          Publishing this version will make it the <span style={{fontWeight: 'bold'}}>effective</span> version
          of the {publishableModelTypeName} and <span style={{fontWeight: 'bold'}}>available for use</span>.
          Any existing effective version will be <span style={{fontWeight: 'bold'}}>superseded</span>.
        </p>
      );
    }
    return (
      <p>
        Publishing this version will make it the <span style={{fontWeight: 'bold'}}>effective</span> version
        of the {publishableModelTypeName} and <span style={{fontWeight: 'bold'}}>available for use</span>.
      </p>
    );
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return (
      <p>
        Withdrawing the effective version will make this {publishableModelTypeName}{' '}
        <span style={{fontWeight: 'bold'}}>unavailable for use</span> until a new effective version is
        published.
      </p>
    );
  }
  throw new Error(`Invalid action: ${action}`);
}

function getVersionNameErrorMessage(
  versionName: string,
  previousVersionNames: Set<string>
): string | undefined {
  if (previousVersionNames.has(versionName)) {
    return 'Name is already in use';
  }
  if (!versionName) {
    return 'Name is required';
  }
  if (versionName.length > 20) {
    return 'Maximum 20 characters';
  }
  return undefined;
}

function shouldPublishingRecordUseControlledName(publishingRecord?: PublishingRecordForButton): boolean {
  if (publishingRecord == null) {
    return true;
  }
  const {index, versionName} = publishingRecord;
  // The initial version name should never use the (SYSGEN) prefix
  if (versionName.startsWith(AUTO_GENERATED_VERSION_NAME_PREFIX)) {
    return true;
  }
  return versionName === `${CONTROLLED_VERSION_NAME_PREFIX} ${index + 1}`;
}

function getInitialCheckboxState(currentPublishingRecord?: PublishingRecordForButton): boolean {
  return shouldPublishingRecordUseControlledName(currentPublishingRecord);
}

function getControlledVersionName(currentPublishingRecord?: PublishingRecordForButton): string {
  if (currentPublishingRecord == null) {
    return DEFAULT_VERSION_NAME;
  }
  return `${CONTROLLED_VERSION_NAME_PREFIX} ${currentPublishingRecord.index + 2}`;
}

function getInitialVersionName(currentPublishingRecord?: PublishingRecordForButton): string {
  return shouldPublishingRecordUseControlledName(currentPublishingRecord)
    ? getControlledVersionName(currentPublishingRecord)
    : currentPublishingRecord!.versionName;
}

export interface TemplatePublishingModalProps {
  publishingAction: PublishableVersionAction;
  onRequestSubmit: ({
    versionName,
    versionDescription,
  }: {
    versionName: string;
    versionDescription?: string;
  }) => void;
  onRequestClose: () => void;
  publishableModelTypeName: string;
  publishingRecords: Array<PublishingRecordForButton>;
}

export default function TemplatePublishingModal({
  publishingAction,
  onRequestSubmit,
  onRequestClose,
  publishableModelTypeName,
  publishingRecords,
}: TemplatePublishingModalProps): JSX.Element {
  const previousVersionNames = useMemo(
    () => new Set(publishingRecords.map((pr) => pr.versionName)),
    [publishingRecords]
  );
  const currentPublishingRecord = publishingRecords[publishingRecords.length - 1];
  const [shouldUseControlledVersionName, setShouldUseControlledVersionName] = React.useState(
    getInitialCheckboxState(currentPublishingRecord)
  );
  const [versionName, setVersionName] = React.useState(getInitialVersionName(currentPublishingRecord));
  const [versionNameError, setVersionNameError] = React.useState<string | undefined>(
    getVersionNameErrorMessage(versionName, previousVersionNames)
  );
  const [versionDescription, setVersionDescription] = React.useState('');
  const actionButton = getButtonPropsFromAction(publishingAction, Boolean(versionNameError));
  const title = getTitleFromAction(publishingAction);
  const hasEffectiveVersion =
    currentPublishingRecord?.publishingStatus === PublishableVersionStatus.EFFECTIVE;
  const description = getDescriptionFromAction(
    publishingAction,
    publishableModelTypeName,
    hasEffectiveVersion
  );

  const controlledVersionName = useMemo(
    () => getControlledVersionName(currentPublishingRecord),
    [currentPublishingRecord]
  );
  const handleCheckboxChange = useCallback(
    (e: InputChangeEvent): void => {
      const checked = e.target.checked;
      setShouldUseControlledVersionName(checked);
      if (checked) {
        setVersionName(controlledVersionName);
        setVersionNameError(getVersionNameErrorMessage(controlledVersionName, previousVersionNames));
      }
    },
    [controlledVersionName, previousVersionNames]
  );

  return (
    <Modal
      title={title}
      medium={true}
      buttons={[actionButton]}
      onRequestSubmit={() => {
        onRequestSubmit({versionName, versionDescription: versionDescription || undefined});
      }}
      onRequestClose={onRequestClose}
      dataTestAttrs={{component: 'TemplatePublishingModal', element: 'root'}}
    >
      {description}
      {publishingAction === PublishableVersionAction.PUBLISH && (
        <div className={css(styles.versionNameContainer)}>
          <TextField
            placeholder="Version display name"
            value={versionName}
            onChange={(e) => {
              setVersionName(e.target.value);
              setVersionNameError(getVersionNameErrorMessage(e.target.value, previousVersionNames));
            }}
            label="Version display name"
            maxLength={20}
            isRequired={true}
            error={{
              message: versionNameError ?? 'Maximum 20 characters',
              style: versionNameError ? InputErrorMessageStyle.ERROR : InputErrorMessageStyle.INFO,
            }}
            hasError={Boolean(versionNameError)}
            disabled={shouldUseControlledVersionName}
            dataTestAttrs={{component: 'TemplatePublishingModal', element: 'textField'}}
          />
          <CheckboxInput
            id="useControlledVersionName"
            checked={shouldUseControlledVersionName}
            label={`Use controlled display name (${controlledVersionName})`}
            onChange={handleCheckboxChange}
            dataTestAttrs={{component: 'TemplatePublishingModal', element: 'checkbox'}}
          />
        </div>
      )}
      <TemplatePublishingChipsWithArrow
        publishingAction={publishingAction}
        currentPublishingRecord={currentPublishingRecord}
        versionName={versionName}
      />
      {publishingAction === PublishableVersionAction.PUBLISH && (
        <div className={css(styles.versionDescriptionContainer)}>
          <FieldLabel label="Description" />
          <TextArea
            onChange={(e) => {
              setVersionDescription(e.target.value);
            }}
            placeholder="Version description"
            value={versionDescription}
            rows={5}
            maxLength={255}
            dataTestAttrs={{component: 'TemplatePublishingModal', element: 'textArea'}}
          />
          <span className={css(styles.maxLengthText)}>Maximum 255 characters</span>
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  maxLengthText: {
    ...FONT_PROXIMA_11,
    color: GRAY_SEMIDARK,
  },
  versionDescriptionContainer: {
    marginTop: '16px',
  },
  versionNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  withdrawActionButton: {
    backgroundColor: PUBLISHABLE_VERSION_STATUS_TO_COLOR[PublishableVersionStatus.WITHDRAWN],
  },
});
